import { useCallback, useEffect, useMemo, useState } from 'react';
import { authClient } from '../utils/amplifyClient';
import { useNavigate } from 'react-router-dom';
import '../css/EquipmentContent.css';

import * as Sentry from '@sentry/react';

import { formatDateDisplay, deleteImageFromS3 } from '../utils/helperFunctions';
import { LOCAL_STORAGE_KEYS } from '../utils/constants';

import { useUserContext } from '../contexts/UserContext';

import {
    deleteEquipmentCategory as deleteEquipmentCategoryMutation,
} from '../graphql/mutations';
import { listFormByCompanyId as listFormByCompanyIdQuery } from '../graphql/queries';
import {
    Form as FormType,
    RegionalDivision,
    Location,
    SingleUnit,
} from '../types/schema';
import { tGroupedEquipment } from '../types';

import { getUrl } from 'aws-amplify/storage';
import { useEquipmentContext } from '../contexts/EquipmentContext';
import { useRegionAndLocationContext } from '../contexts/RegionAndLocationContext';
import { CustomModal } from './CustomModal';
import {
    getDownReasonPieSlices,
    parseDownReasonEntries,
    type DownReasonEntry,
} from './pages-equipment/equipmentDownReasonUtils';
import { getEquipmentCategoryEditPath } from './pages-equipment/equipmentConstants';
import {
    collectCompanyLocationIds,
    isUnitMissingOrUnknownLocation,
    isUnitVisibleOnEquipmentListForUser,
    isUnitArchived,
} from './pages-equipment/equipmentFieldUtils';
import { getUnitNotes, UNIT_NOTES_EXTRA_LABEL } from '../utils/unitNotes';
import {
    shouldIncludeEquipmentCategoryOnList,
    shouldShowAllCompanyEquipmentOnList,
    canViewEmptyEquipmentCategories,
    getEquipmentAssignmentScope,
    getEquipmentListLocations,
    getEquipmentListRegionTabs,
} from '../utils/equipmentUnitUtils';

import {
    Btn,
    BtnIconOnly,
    Card,
    PageHeader,
    IconQRcode,
    IconPlus,
    IconPlusCircle,
    IconPencil,
    IconPrint,
    IconTrash,
    TabNav,
} from './ui';

interface ExtendedEquipment extends tGroupedEquipment {
    displayUnits?: (SingleUnit | null)[];
}

const deleteSingleUnitSimple = /* GraphQL */ `
    mutation DeleteSingleUnit($input: DeleteSingleUnitInput!) {
        deleteSingleUnit(input: $input) {
            id
        }
    }
`;

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

type ExtraUnitInfoEntry = { label: string; value: string };

const parseExtraUnitInfo = (raw: string | null | undefined): ExtraUnitInfoEntry[] => {
    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const preloadImage = (src: string) =>
    new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });

const waitForPrintWindowImages = (printWindow: Window) => {
    const images = Array.from(printWindow.document.images);
    if (images.length === 0) {
        return Promise.resolve();
    }

    return Promise.all(
        images.map(
            (img) =>
                new Promise<void>((resolve) => {
                    if (img.complete) {
                        resolve();
                        return;
                    }
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                }),
        ),
    );
};

const writePrintDocument = (printWindow: Window, htmlContent: string) => {
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
};

const getExpandedCategoriesStorageKey = (companyId: string) =>
    `${LOCAL_STORAGE_KEYS.EQUIPMENT_EXPANDED_CATEGORIES}:${companyId}`;

const readStoredExpandedCategories = (companyId: string): Set<string> => {
    try {
        const raw = localStorage.getItem(getExpandedCategoriesStorageKey(companyId));
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []);
    } catch {
        return new Set();
    }
};

const writeStoredExpandedCategories = (companyId: string, categoryIds: Set<string>) => {
    localStorage.setItem(getExpandedCategoriesStorageKey(companyId), JSON.stringify([...categoryIds]));
};

// TODO: for the drop down category name, I need to only include category names that aren't used by categories for this company, only for categories from other companies.

export const EquipmentContent = () => {
    const [, setFormsList] = useState<FormType[] | []>([]);
    // Gen2 Location omits the residual Gen1 `districtId`; migrated records may still carry it at runtime.
    const [allLocations, setAllLocations] = useState<(Location & { districtId?: string | null })[]>([]);
    const [allRegions, setAllRegions] = useState<RegionalDivision[] | []>([]);
    const [locationsList, setLocationsList] = useState<Location[] | []>([]);
    const [regions, setRegions] = useState<RegionalDivision[] | []>([]);
    const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});
    const [isDeleting, setIsDeleting] = useState(false);
    const [, setIsLoadingFormsList] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState<string>('all');
    const [unitListMode, setUnitListMode] = useState<'active' | 'archived'>('active');
    const [filteredEquipmentList, setFilteredEquipmentList] = useState<ExtendedEquipment[]>([]);
    const [selectToDeleteEquipmentUnit, setSelectToDeleteEquipmentUnit] = useState<SingleUnit | null>(null);

    const { companyUser, company, companyOwner, businessAdmin } = useUserContext();
    const companyId = (company?.id as string) || companyOwner?.companyId;

    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() =>
        companyId ? readStoredExpandedCategories(companyId) : new Set(),
    );
    const [selectedCategories, setSelectedCategories] = useState<tGroupedEquipment[]>([]);

    const {
        companyRegions,
        companyLocations,
        companyDistricts,
        allRegions: contextAllRegions,
        allLocations: contextAllLocations,
    } = useRegionAndLocationContext();
    const { companyGroupedEquipmentData, companyUnits, refreshEquipment } = useEquipmentContext();

    const companyLocationIds = useMemo(() => collectCompanyLocationIds(companyLocations), [companyLocations]);

    const viewAllCompanyEquipment = useMemo(
        () => shouldShowAllCompanyEquipmentOnList(companyUser, companyOwner, businessAdmin),
        [companyUser, companyOwner, businessAdmin],
    );

    const includeEmptyEquipmentCategories = useMemo(
        () => canViewEmptyEquipmentCategories(companyUser, companyOwner, businessAdmin),
        [companyUser, companyOwner, businessAdmin],
    );

    const includeEquipmentCategoryOnList = useCallback(
        (equipment: Parameters<typeof shouldIncludeEquipmentCategoryOnList>[0]) =>
            shouldIncludeEquipmentCategoryOnList(equipment, {
                includeEmptyCategories: includeEmptyEquipmentCategories,
            }),
        [includeEmptyEquipmentCategories],
    );

    // const location = useLocation();
    const navigate = useNavigate();

    const [, setAddedUnits] = useState<SingleUnit[]>([]);
    const [categoryToDelete, setCategoryToDelete] = useState<tGroupedEquipment | null>(null);
    const [unitModalMode] = useState<'batch-add' | 'single-add' | 'edit' | null>(null);

    const hasUnassignedEquipment = useMemo(() => {
        const result = companyGroupedEquipmentData.some((item) => {
            return (
                item.units &&
                item.units.length > 0 &&
                item.units.some((unit) => unit && isUnitMissingOrUnknownLocation(unit, companyLocationIds))
            );
        });

        return result;
    }, [companyGroupedEquipmentData, companyLocationIds]);

    const tabs = [
        {
            id: 'all',
            label: 'All Equipment',
        },
        ...(hasUnassignedEquipment
            ? [
                  {
                      id: 'unassigned',
                      label: 'Unassigned',
                  },
              ]
            : []),
        ...regions.map((region) => ({
            id: region.id,
            label: region.name,
        })),
    ];

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            if (companyId) {
                writeStoredExpandedCategories(companyId, newSet);
            }
            return newSet;
        });
    };

    useEffect(() => {
        if (!companyId) return;

        const stored = readStoredExpandedCategories(companyId);
        if (companyGroupedEquipmentData.length === 0) {
            setExpandedCategories(stored);
            return;
        }

        const validCategoryIds = new Set(companyGroupedEquipmentData.map((category) => category.id));
        const pruned = new Set([...stored].filter((id) => validCategoryIds.has(id)));

        setExpandedCategories(pruned);
        if (pruned.size !== stored.size) {
            writeStoredExpandedCategories(companyId, pruned);
        }
    }, [companyId, companyGroupedEquipmentData]);

    const getFormsList = async () => {
        if (!companyId) {
            return;
        }
        setIsLoadingFormsList(true);
        try {
            const result = await authClient.graphql({
                query: listFormByCompanyIdQuery,
                variables: {
                    companyId: companyId as string,
                },
            });
            setFormsList(result.data.listFormByCompanyId.items.sort((a, b) => a.name.localeCompare(b.name)));
        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
        } finally {
            setIsLoadingFormsList(false);
        }
    };

    const getEquipmentImageUrl = async () => {
        try {
            await Promise.all(
                companyGroupedEquipmentData.map(async (item) => {
                    try {
                        const photoUrl = item?.image
                            ? await getUrl({
                                  path: `${item.image}`,
                              })
                            : null;
                        const path = photoUrl?.url.pathname;
                        const url = photoUrl?.url.href;
                        if (
                            path?.includes('.png') ||
                            path?.includes('.jpeg') ||
                            path?.includes('.jpg') ||
                            path?.includes('.JPG')
                        ) {
                            setImageUrls((prev) => ({
                                ...prev,
                                [item.id]: url ? url : '',
                            }));
                        }
                    } catch (error) {
                        console.error('Error fetching image URL:', error);
                        Sentry.captureException(error);
                    }
                }),
            );
        } catch (error) {
            console.error('Error in getEquipmentImageUrl:', error);
            Sentry.captureException(error);
        }
    };

    const assignmentScope = useMemo(
        () => getEquipmentAssignmentScope(companyUser, companyLocations, companyRegions, companyOwner, businessAdmin),
        [businessAdmin, companyLocations, companyOwner, companyRegions, companyUser],
    );

    const getLocationsList = async () => {
        if (!companyId) {
            return;
        }

        setAllRegions(contextAllRegions);
        setAllLocations(contextAllLocations);
        setRegions(
            getEquipmentListRegionTabs(assignmentScope, companyRegions, companyLocations, companyUnits),
        );
        setLocationsList(getEquipmentListLocations(assignmentScope, companyLocations));
    };

    useEffect(() => {
        getFormsList();
        getLocationsList();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        companyId,
        assignmentScope,
        companyRegions,
        companyLocations,
        companyUnits,
        contextAllRegions,
        contextAllLocations,
    ]);

    useEffect(() => {
        if (selectedRegion === 'all') {
            return;
        }
        if (selectedRegion === 'unassigned') {
            if (!hasUnassignedEquipment) {
                setSelectedRegion('all');
            }
            return;
        }
        if (!regions.some((region) => region.id === selectedRegion)) {
            setSelectedRegion('all');
        }
    }, [hasUnassignedEquipment, regions, selectedRegion]);

    useEffect(() => {
        if (companyId) {
            refreshEquipment();
        }
    }, [companyId]);

    useEffect(() => {
        if (companyGroupedEquipmentData.length > 0 && Object.keys(imageUrls).length === 0) {
            getEquipmentImageUrl();
        }
    }, [companyGroupedEquipmentData]);

    const filterEquipmentByRegion = (regionId: string) => {
        if (viewAllCompanyEquipment) {
            if (regionId === 'all') {
                const filtered = companyGroupedEquipmentData
                    .filter(includeEquipmentCategoryOnList)
                    .map((equipment) => {
                        const equipmentCopy: ExtendedEquipment = {
                            ...equipment,
                            displayUnits: equipment.units, // Set displayUnits to all units
                        };
                        return equipmentCopy;
                    });

                setFilteredEquipmentList(filtered);
                return;
            } else if (regionId === 'unassigned') {
                const unassignedEquipment = companyGroupedEquipmentData
                    .filter(includeEquipmentCategoryOnList)
                    .map((equipment) => {
                        const equipmentCopy: ExtendedEquipment = {
                            ...equipment,
                        };

                        if (equipment.units && equipment.units.length > 0) {
                            const unassignedUnits = equipment.units.filter((unit) =>
                                unit ? isUnitMissingOrUnknownLocation(unit, companyLocationIds) : false,
                            );
                            if (unassignedUnits.length > 0) {
                                equipmentCopy.displayUnits = unassignedUnits;
                                return equipmentCopy;
                            }
                        }
                        return null;
                    })
                    .filter((item): item is ExtendedEquipment => item !== null);

                setFilteredEquipmentList(unassignedEquipment);
                return;
            } else {
                // Filter by specific region without location assignment restrictions
                const filtered = companyGroupedEquipmentData
                    .filter(includeEquipmentCategoryOnList)
                    .map((equipment) => {
                        const equipmentCopy: ExtendedEquipment = {
                            ...equipment,
                        };

                        if (equipment.units && equipment.units.length > 0) {
                            const filteredUnits = equipment.units.filter(
                                (unit) =>
                                    unit?.locationId &&
                                    locationsList.find(
                                        (loc) => loc.id === unit.locationId && loc.regionalDivisionId === regionId,
                                    ),
                            );

                            if (filteredUnits.length > 0) {
                                equipmentCopy.displayUnits = filteredUnits;
                                return equipmentCopy;
                            }
                        }
                        return null;
                    })
                    .filter((item): item is ExtendedEquipment => item !== null);

                setFilteredEquipmentList(filtered);
                return;
            }
        }

        // If no assignments exist, show all equipment (no location-based filtering)
        if (!regions.length && !locationsList.length) {
            const filtered = companyGroupedEquipmentData
                .filter(includeEquipmentCategoryOnList)
                .map((equipment) => {
                    const equipmentCopy: ExtendedEquipment = {
                        ...equipment,
                        displayUnits: equipment.units,
                    };
                    return equipmentCopy;
                });
            setFilteredEquipmentList(filtered);
            return;
        }

        if (regionId === 'all') {
            // For 'all', show all assigned equipment AND unassigned equipment
            const filtered = companyGroupedEquipmentData
                .map((equipment) => {
                    const equipmentCopy: ExtendedEquipment = {
                        ...equipment,
                        units: [...(equipment.units || [])],
                    };

                    if (equipment.units && equipment.units.length > 0) {
                        // Include both assigned AND unassigned units
                        const filteredUnits = equipment.units.filter(
                            (unit) =>
                                unit && isUnitVisibleOnEquipmentListForUser(unit, locationsList, companyLocationIds),
                        );

                        if (filteredUnits.length > 0) {
                            equipmentCopy.displayUnits = filteredUnits;
                            return equipmentCopy;
                        }
                    }
                    return null;
                })
                .filter((item): item is ExtendedEquipment => item !== null);

            setFilteredEquipmentList(filtered);
        } else if (regionId === 'unassigned') {
            // No changes needed for unassigned - keep existing logic
            const unassignedEquipment = companyGroupedEquipmentData
                .map((equipment) => {
                    const equipmentCopy: ExtendedEquipment = {
                        ...equipment,
                        units: [...(equipment.units || [])],
                    };

                    if (equipment.units && equipment.units.length > 0) {
                        const unassignedUnits = equipment.units.filter((unit) =>
                            unit ? isUnitMissingOrUnknownLocation(unit, companyLocationIds) : false,
                        );
                        if (unassignedUnits.length > 0) {
                            equipmentCopy.displayUnits = unassignedUnits;
                            return equipmentCopy;
                        }
                    }
                    return null;
                })
                .filter((item): item is ExtendedEquipment => item !== null);

            setFilteredEquipmentList(unassignedEquipment);
        } else {
            // Filter by specific region, but only from assigned locations
            const filtered = companyGroupedEquipmentData
                .map((equipment) => {
                    const equipmentCopy: ExtendedEquipment = {
                        ...equipment,
                        units: [...(equipment.units || [])],
                    };

                    if (equipment.units && equipment.units.length > 0) {
                        // Filter units for this region AND only from assigned locations
                        const filteredUnits = equipment.units.filter(
                            (unit) =>
                                unit?.locationId &&
                                locationsList.find(
                                    (loc) => loc.id === unit.locationId && loc.regionalDivisionId === regionId,
                                ),
                        );

                        if (filteredUnits.length > 0) {
                            equipmentCopy.displayUnits = filteredUnits;
                            return equipmentCopy;
                        }
                    }
                    return null;
                })
                .filter((item): item is ExtendedEquipment => item !== null);

            setFilteredEquipmentList(filtered);
        }
    };
    // useEffect to handle initial filtering and updates
    useEffect(() => {
        filterEquipmentByRegion(selectedRegion);
    }, [
        companyGroupedEquipmentData,
        selectedRegion,
        locationsList,
        companyLocationIds,
        companyUser,
        regions,
        viewAllCompanyEquipment,
        includeEquipmentCategoryOnList,
    ]);

    const handleDeleteSingleUnit = async (unitToDelete: SingleUnit, skipRefresh = false) => {
        if (!unitToDelete?.id) return;

        try {
            // Delete the SingleUnit directly
            await authClient.graphql({
                query: deleteSingleUnitSimple,
                variables: {
                    input: { id: unitToDelete.id },
                },
            });

            // Delete image from S3 if it exists
            if (unitToDelete.image) {
                try {
                    await deleteImageFromS3(unitToDelete.image);
                } catch (error) {
                    console.error('Error deleting image from S3:', error);
                    Sentry.captureException(error);
                }
            }

            // If in batch-add mode, also remove from addedUnits table
            if (unitModalMode === 'batch-add') {
                setAddedUnits((prev) => prev.filter((unit) => unit.id !== unitToDelete.id));
            }

            // Refresh equipment data but only after last unit is deleted if bulk deletion
            if (!skipRefresh) {
                await refreshEquipment();
            }
        } catch (error) {
            console.error('Error deleting unit:', error);
            Sentry.captureException(error);
        }
    };

    const handleDeleteCategory = async () => {
        if (!categoryToDelete) return;

        setIsDeleting(true);
        try {
            const categoryUnits = categoryToDelete.units || [];

            for (const unit of categoryUnits) {
                if (unit?.id) {
                    try {
                        await handleDeleteSingleUnit(unit, true);
                    } catch (error) {
                        console.error(`Error deleting unit ${unit.id}:`, error);
                    }
                }
            }

            await authClient.graphql({
                query: deleteEquipmentCategoryMutation,
                variables: {
                    input: { id: categoryToDelete.id },
                },
            });

            if (categoryToDelete.image) {
                try {
                    await deleteImageFromS3(categoryToDelete.image);
                } catch (error) {
                    console.error('Error deleting category image:', error);
                }
            }

            await refreshEquipment();
            setCategoryToDelete(null);
        } catch (error) {
            console.error('Error deleting category:', error);
            Sentry.captureException(error);
        } finally {
            setIsDeleting(false);
        }
    };

    const createPieChartSVG = (data: DownReasonEntry[]) => {
        if (!data || data.length === 0) return '';

        const { slices: pieData, total } = getDownReasonPieSlices(data);
        if (pieData.length === 0) return '<p>No issues recorded</p>';

        let currentAngle = 0;
        const radius = 80;
        const centerX = 100;
        const centerY = 100;

        const slices = pieData
            .map((item) => {
                const sliceAngle = (item.value / total) * 360;
                const startAngle = currentAngle;
                const endAngle = currentAngle + sliceAngle;

                const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
                const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
                const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
                const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

                const largeArc = sliceAngle > 180 ? 1 : 0;

                const pathData = [
                    `M ${centerX} ${centerY}`,
                    `L ${x1} ${y1}`,
                    `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
                    'Z',
                ].join(' ');

                currentAngle += sliceAngle;

                return `<path d="${pathData}" fill="${item.color}" stroke="white" stroke-width="2"/>`;
            })
            .join('');

        const legend = pieData
            .map(
                (item) =>
                    `<div style="display: flex; align-items: center; margin: 5px 0;">
     <div style="
      width: 10px;
      height: 10px;
      border: 6px solid ${item.color}; 
      margin-right: 10px;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
    "></div>
      <span>${item.label}: ${item.value} (${((item.value / total) * 100).toFixed(1)}%)</span>
    </div>`,
            )
            .join('');

        return `
    <div style="margin: 20px 0;">
      <h4>Distribution of ${total} Issues</h4>
      <div style="display: flex; align-items: center; gap: 20px;">
        <svg width="200" height="200" viewBox="0 0 200 200">
          ${slices}

        </svg>
        <div>${legend}</div>
      </div>
    </div>
  `;
    };

    const handlePrintUnitClick = async (unit: SingleUnit) => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            window.alert('Please allow pop-ups to print this equipment report.');
            return;
        }

        writePrintDocument(
            printWindow,
            `<!DOCTYPE html><html><head><title>Preparing report…</title></head><body><p>Preparing report…</p></body></html>`,
        );

        try {
            const categoryData = companyGroupedEquipmentData.find((cat) => cat.id === unit.categoryId);
            const equipName = categoryData?.name || 'Equipment';
            const categoryImageUrl = categoryData?.id ? imageUrls[categoryData.id] : '';
            const parsedExtraInfo = parseExtraUnitInfo(unit.extraUnitInfo).filter(
                (info) => info.label !== UNIT_NOTES_EXTRA_LABEL,
            );
            const unitNotes = getUnitNotes(unit.extraUnitInfo);

            let unitImage = '';
            if (unit.image) {
                try {
                    const photoUrl = await getUrl({ path: unit.image });
                    unitImage = photoUrl?.url.href || '';
                } catch (error) {
                    console.error('Error loading unit image for print:', error);
                    Sentry.captureException(error);
                }
            }

            const downReasonEntries = parseDownReasonEntries(unit.downReason);
            let pieChartHTML = '';
            if (unit.downReason) {
                if (downReasonEntries === null) {
                    pieChartHTML = '<p>Invalid issue classification data</p>';
                } else {
                    pieChartHTML = createPieChartSVG(downReasonEntries);
                }
            }

            const imagesToPreload = [categoryImageUrl, unitImage].filter(Boolean);
            if (imagesToPreload.length > 0) {
                await Promise.all(imagesToPreload.map((src) => preloadImage(src)));
            }

            const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Equipment Report - ${escapeHtml(equipName)}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #0f172a; }
        .header { text-align: center; margin-bottom: 30px; }
        .equipment-info { margin-bottom: 30px; display: flex; gap: 20px; flex-wrap: wrap; }
        .equipment-photo { max-width: 200px; margin: 10px 0; }
        .units-section { margin-top: 30px; }
        .unit { border: 1px solid #ccc; margin: 20px 0; padding: 15px; }
        .unit-header { font-weight: bold; font-size: 18px; margin-bottom: 10px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .info-item { margin: 5px 0; }
        .label { font-weight: bold; }
        .extra-info { margin-top: 15px; }
        .extra-info-item { margin: 5px 0; }
        .pie-chart-section {
          page-break-inside: avoid;
          break-inside: avoid;
          margin-top: 20px;
        }
        @media print {
          body { margin: 0; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Equipment Report</h1>
        <h2>${escapeHtml(equipName)}</h2>
      </div>

      <div class="equipment-info">
        ${categoryImageUrl ? `<img src="${escapeHtml(categoryImageUrl)}" alt="Equipment type photo" class="equipment-photo" />` : ''}
        ${unitImage ? `<img src="${escapeHtml(unitImage)}" alt="Unit photo" class="equipment-photo" />` : ''}
      </div>

      <div class="units-section">
        <div class="unit">
          <div class="unit-header">Unit Name: ${escapeHtml(unit.name || 'Unnamed unit')}</div>
          <div class="info-grid">
            ${unit.serialNumber ? `<div class="info-item"><span class="label">Serial Number:</span> ${escapeHtml(unit.serialNumber)}</div>` : ''}
            ${unit.manufacturerName ? `<div class="info-item"><span class="label">Manufacturer:</span> ${escapeHtml(unit.manufacturerName)}</div>` : ''}
            ${unit.manufactureDate ? `<div class="info-item"><span class="label">Manufacture Date:</span> ${escapeHtml(unit.manufactureDate)}</div>` : ''}
            ${unit.aquisitionDate ? `<div class="info-item"><span class="label">Acquisition Date:</span> ${escapeHtml(unit.aquisitionDate)}</div>` : ''}
            ${unit.description ? `<div class="info-item"><span class="label">Description:</span> ${escapeHtml(unit.description)}</div>` : ''}
            ${unit.unitOfMeasure ? `<div class="info-item"><span class="label">Unit of Measure:</span> ${escapeHtml(unit.unitOfMeasure)}</div>` : ''}
            ${unit.currentMachineHours != null ? `<div class="info-item"><span class="label">Current ${escapeHtml(unit.unitOfMeasure || 'Hours')}:</span> ${escapeHtml(String(unit.currentMachineHours))}</div>` : ''}
            ${unit.currentUMCounterValue != null ? `<div class="info-item"><span class="label">Current UM Counter:</span> ${escapeHtml(String(unit.currentUMCounterValue))}</div>` : ''}
            ${unit.machineHoursAtAquisition != null ? `<div class="info-item"><span class="label">${escapeHtml(unit.unitOfMeasure || 'Hours')} at Acquisition:</span> ${escapeHtml(String(unit.machineHoursAtAquisition))}</div>` : ''}
            ${unit.machineUMCounterAtAquisition != null ? `<div class="info-item"><span class="label">UM Counter at Acquisition:</span> ${escapeHtml(String(unit.machineUMCounterAtAquisition))}</div>` : ''}
            ${unit.lastServiceHours != null ? `<div class="info-item"><span class="label">Last Service ${escapeHtml(unit.unitOfMeasure || 'Hours')}:</span> ${escapeHtml(String(unit.lastServiceHours))}</div>` : ''}
            ${unit.lastInspectionDate ? `<div class="info-item"><span class="label">Last Inspection:</span> ${escapeHtml(formatDateDisplay(unit.lastInspectionDate))}</div>` : ''}
            ${unit.replacedUMCounterStatus ? `<div class="info-item"><span class="label">UM Counter Replaced:</span> Yes</div>` : ''}
            ${unit.replacedUMCounterValue != null ? `<div class="info-item"><span class="label">Replaced UM Counter Value:</span> ${escapeHtml(String(unit.replacedUMCounterValue))}</div>` : ''}
            ${unit.locationId ? `<div class="info-item"><span class="label">Location:</span> ${escapeHtml(getLocationName(unit.locationId))}</div>` : ''}
          </div>

          ${
              unitNotes
                  ? `<hr/><div class="extra-info"><h4>Notes</h4><div class="extra-info-item">${escapeHtml(unitNotes).replace(/\n/g, '<br/>')}</div></div>`
                  : ''
          }
          ${
              parsedExtraInfo.length > 0
                  ? `
              <hr/>
              <div class="extra-info">
                <h4>Additional Information</h4>
                ${parsedExtraInfo
                    .map(
                        (info) => `
                  <div class="extra-info-item">
                    <span class="label">${escapeHtml(info.label || '')}:</span> ${escapeHtml(String(info.value ?? ''))}
                  </div>
                `,
                    )
                    .join('')}
              </div>
            `
                  : ''
          }
          ${
              unit.downReason
                  ? `
            <div class="pie-chart-section">
              <hr/>
              <div class="info-item">
                <span class="label">Down Reason Classifications:</span>
                ${pieChartHTML}
              </div>
            </div>`
                  : ''
          }
        </div>
      </div>
    </body>
    </html>
  `;

            writePrintDocument(printWindow, htmlContent);

            const triggerPrint = async () => {
                await waitForPrintWindowImages(printWindow);
                printWindow.focus();
                printWindow.print();
            };

            if (printWindow.document.readyState === 'complete') {
                await triggerPrint();
            } else {
                printWindow.onload = () => {
                    void triggerPrint();
                };
            }
        } catch (error) {
            console.error('Error generating equipment print report:', error);
            Sentry.captureException(error);
            writePrintDocument(
                printWindow,
                `<!DOCTYPE html><html><head><title>Print error</title></head><body><p>Unable to generate the equipment report. Please try again.</p></body></html>`,
            );
        }
    };

    const getLocationName = (locationId: string) => {
        const location = allLocations.find((loc) => loc.id === locationId);
        const district = companyDistricts.find((d) => d.id === location?.districtId);
        const region = allRegions.find((reg) => reg.id === location?.regionalDivisionId);
        if (!location) return '--';
        const parts = [location.name];
        if (district) parts.push(district.name);
        if (region) parts.push(region.name);
        return parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(' · ')})` : parts[0];
    };

    return (
        <div className="page-container equipment-page">
            <PageHeader
                title="Equipment"
                actions={
                    <>
                        <Btn
                            variant="primary"
                            buttonSize="lg"
                            buttonClasses="btn-add"
                            buttonText="Add New Equipment"
                            buttonIcon={<IconPlusCircle iconClasses="btn-add-icon" />}
                            buttonOnClick={() => navigate('/equipment/new')}
                        />

                        <Btn
                            variant="secondary-outlined"
                            buttonSize="lg"
                            buttonClasses="btn-add"
                            buttonText="Add New Category"
                            buttonIcon={<IconPlusCircle iconClasses="btn-add-icon" />}
                            buttonOnClick={() => navigate('/equipment/category/new')}
                        />

                        <Btn
                            variant="secondary-outlined"
                            buttonSize="lg"
                            buttonClasses="btn-QR"
                            buttonText={`Generate QR codes (${selectedCategories.length})`}
                            buttonIcon={<IconQRcode iconClasses="btn-qr-icon" />}
                            buttonTitle={
                                selectedCategories.length === 0
                                    ? 'Please select 1+ category first'
                                    : 'Generate QR codes for selected categories'
                            }
                            ariaLabel="create qr button"
                            buttonDisabled={selectedCategories.length === 0}
                            buttonOnClick={() => {
                                sessionStorage.setItem('equipmentList', JSON.stringify(selectedCategories));
                                navigate('/qr-codes');
                            }}
                        />
                    </>
                }
            />
            <Card variant="card-xl" className="equipment-tabs-card">
                {/* Custom Tabs */}
                <TabNav
                    variant="pills"
                    ariaLabel="Equipment tabs"
                    className="equipment-tabs tab-nav-stand-alone"
                    tabButtonClasses="equipment-tab"
                    tabs={tabs}
                    activeTabId={selectedRegion}
                    onTabChange={setSelectedRegion}
                />

                <div className="equipment-archive-switch" role="group" aria-label="Show units">
                    <Btn
                        variant={unitListMode === 'active' ? 'primary' : 'secondary'}
                        buttonSize="sm"
                        buttonText="In service"
                        buttonOnClick={() => setUnitListMode('active')}
                        buttonAriaProps={{ 'aria-pressed': unitListMode === 'active' }}
                    />
                    <Btn
                        variant={unitListMode === 'archived' ? 'primary' : 'secondary'}
                        buttonSize="sm"
                        buttonText="Archived"
                        buttonOnClick={() => setUnitListMode('archived')}
                        buttonAriaProps={{ 'aria-pressed': unitListMode === 'archived' }}
                    />
                </div>

                {/* Category Cards */}
                {(() => {
                    const categoriesForList = filteredEquipmentList
                        .map((category) => {
                            const source = category.displayUnits || category.units || [];
                            const units = source.filter((unit): unit is SingleUnit => {
                                if (!unit) return false;
                                return unitListMode === 'archived' ? isUnitArchived(unit) : !isUnitArchived(unit);
                            });
                            return { category, units };
                        })
                        .filter(({ units }) => unitListMode === 'active' || units.length > 0);
                    if (categoriesForList.length === 0) {
                        return (
                            <div className="ec-category-empty">
                                <p className="ec-category-empty-text">
                                    {unitListMode === 'archived'
                                        ? 'No archived units.'
                                        : 'No equipment assigned to this location yet'}
                                </p>
                            </div>
                        );
                    }
                    return categoriesForList.map(({ category, units }) => {
                        const isExpanded = expandedCategories.has(category.id);
                        const unitCount = units.length;
                        const isCategorySelected = selectedCategories.some((selected) => selected.id === category.id);

                        return (
                            <Card key={category.id} variant="card-sm" className="ec-category-card">
                                <div className="ec-category-header">
                                    <div
                                        className="ec-category-header-left"
                                        onClick={() => toggleCategory(category.id)}
                                    >
                                        <span className="ec-collapse-icon">{isExpanded ? '▼' : '▶'}</span>
                                        <div className="ec-category-info">
                                            <span className="ec-category-name">{category.name}</span>
                                            <span className="ec-category-meta">
                                                {unitCount} unit{unitCount !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="ec-category-header-actions">
                                        <BtnIconOnly
                                            size="sm"
                                            fill="opaque"
                                            icon={<IconQRcode />}
                                            ariaLabel={`Select ${category.name} for QR code generation`}
                                            buttonTooltip="Select for QR codes"
                                            buttonClasses={isCategorySelected ? 'active' : undefined}
                                            buttonAriaProps={{ 'aria-pressed': isCategorySelected }}
                                            onClick={() => {
                                                setSelectedCategories((prev) =>
                                                    isCategorySelected
                                                        ? prev.filter((selected) => selected.id !== category.id)
                                                        : [...prev, category],
                                                );
                                            }}
                                        />
                                        <BtnIconOnly
                                            size="sm"
                                            fill="opaque"
                                            icon={<IconPencil />}
                                            ariaLabel="Edit equipment type"
                                            onClick={() => {
                                                navigate(getEquipmentCategoryEditPath(category.id));
                                            }}
                                        />
                                        <BtnIconOnly
                                            size="sm"
                                            fill="opaque"
                                            variant="danger"
                                            icon={<IconTrash />}
                                            ariaLabel="Delete equipment"
                                            onClick={() => setCategoryToDelete(category)}
                                        />
                                        {isExpanded && (
                                            <Btn
                                                variant="secondary"
                                                buttonSize="sm"
                                                buttonText="Add Unit"
                                                buttonIcon={<IconPlus />}
                                                buttonOnClick={() => {
                                                    navigate(`/equipment/new?categoryId=${category.id}`);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="ec-category-body">
                                        {unitCount === 0 ? (
                                            <div className="ec-category-empty">
                                                <p className="ec-category-empty-text">
                                                    No equipment for this category yet
                                                </p>
                                                <Btn
                                                    variant="primary"
                                                    buttonSize="sm"
                                                    buttonText="Add Unit"
                                                    buttonIcon={<IconPlus />}
                                                    buttonOnClick={() => {
                                                        navigate(`/equipment/new?categoryId=${category.id}`);
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="ec-unit-cards">
                                                {units.map((unit) => {
                                                    const hasLocationPermission =
                                                        !unit.locationId ||
                                                        companyUser?.locationAssignment?.includes(unit.locationId) ||
                                                        companyOwner;

                                                    return (
                                                        <Card key={unit.id} variant="card-sm" className="ec-unit-card">
                                                            <div className="ec-unit-card-text">
                                                                <Btn
                                                                    buttonAs="a"
                                                                    variant="link"
                                                                    buttonClasses="ec-unit-card-title"
                                                                    buttonText={unit.name}
                                                                    href={`/equipment/${unit.id}`}
                                                                    buttonOnClick={(event) => {
                                                                        event.preventDefault();
                                                                        navigate(`/equipment/${unit.id}`);
                                                                    }}
                                                                />
                                                                {unit.description && (
                                                                    <p className="ec-unit-card-description">
                                                                        {unit.description}
                                                                    </p>
                                                                )}
                                                                {unit.lastInspectionDate && (
                                                                    <div className="ec-unit-card-inspection">
                                                                        <span className="ec-unit-card-inspection-label">
                                                                            Last Inspection:
                                                                        </span>
                                                                        <span className="ec-unit-card-inspection-date">
                                                                            {formatDateDisplay(unit.lastInspectionDate)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="ec-unit-card-actions">
                                                                <BtnIconOnly
                                                                    size="sm"
                                                                    icon={<IconPrint />}
                                                                    ariaLabel={`Print ${unit.name}`}
                                                                    onClick={() => handlePrintUnitClick(unit)}
                                                                />
                                                                <BtnIconOnly
                                                                    size="sm"
                                                                    icon={<IconPencil />}
                                                                    ariaLabel="View / Edit unit"
                                                                    onClick={() =>
                                                                        navigate(`/equipment/${unit.id}?edit=true`)
                                                                    }
                                                                />
                                                                <BtnIconOnly
                                                                    size="sm"
                                                                    variant="danger"
                                                                    icon={<IconTrash />}
                                                                    ariaLabel="Delete unit"
                                                                    buttonTooltip={
                                                                        hasLocationPermission
                                                                            ? 'Delete unit'
                                                                            : 'Not authorized for unit in this location'
                                                                    }
                                                                    disabled={!hasLocationPermission}
                                                                    onClick={() =>
                                                                        setSelectToDeleteEquipmentUnit(unit)
                                                                    }
                                                                />
                                                            </div>
                                                        </Card>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card>
                        );
                    });
                })()}
            </Card>

            {/* modal for deleting unit within equipment classification */}
            <CustomModal
                visible={!!selectToDeleteEquipmentUnit}
                onDismiss={() => setSelectToDeleteEquipmentUnit(null)}
                header={`Delete ${selectToDeleteEquipmentUnit?.name}`}
                footer={
                    <div className="ec-modal-actions">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Cancel"
                            buttonOnClick={() => setSelectToDeleteEquipmentUnit(null)}
                        />
                        <Btn
                            variant="danger"
                            buttonText={isDeleting ? 'Deleting...' : 'Delete Unit'}
                            buttonDisabled={isDeleting}
                            buttonOnClick={async () => {
                                if (!selectToDeleteEquipmentUnit) return;
                                setIsDeleting(true);
                                try {
                                    await handleDeleteSingleUnit(selectToDeleteEquipmentUnit);
                                    setSelectToDeleteEquipmentUnit(null);
                                } catch (error) {
                                    console.error('Error deleting unit:', error);
                                } finally {
                                    setIsDeleting(false);
                                }
                            }}
                        />
                    </div>
                }
            >
                <div className="ec-modal-body-text">
                    <p>
                        Are you sure you want to delete the unit <strong>{selectToDeleteEquipmentUnit?.name}</strong>?
                    </p>
                    <p className="ec-warning-text">This action cannot be undone.</p>
                </div>
            </CustomModal>

            {/* Delete Category Confirmation Modal */}
            <CustomModal
                onDismiss={() => setCategoryToDelete(null)}
                visible={!!categoryToDelete}
                header="Delete Equipment"
                footer={
                    <div className="ec-modal-actions">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Cancel"
                            buttonOnClick={() => setCategoryToDelete(null)}
                        />
                        <Btn
                            variant="danger"
                            buttonText={isDeleting ? 'Deleting...' : 'Delete Equipment'}
                            buttonDisabled={isDeleting}
                            buttonOnClick={handleDeleteCategory}
                        />
                    </div>
                }
            >
                <div className="ec-modal-body-text">
                    <p>
                        <strong>Warning:</strong> This action cannot be undone.
                    </p>
                    <p>
                        Are you sure you want to delete <strong>{categoryToDelete?.name}</strong>?
                    </p>
                    {(categoryToDelete?.units?.length || 0) > 0 && (
                        <p className="ec-warning-text">
                            <strong>
                                All {categoryToDelete?.units.length} associated units will also be permanently deleted.
                            </strong>
                        </p>
                    )}
                </div>
            </CustomModal>
        </div>
    );
};
